<template>
  <section class="terminal-payment-view">
    <h2 class="terminal-payment-title">
      {{ currentState.title }}
    </h2>
    <div v-if="currentState.icon" class="text-center">
      <component :is="currentState.icon" style="max-width: 150px; max-height: 150px;"></component>
    </div>
    <div v-if="description" class="terminal-payment-description">
      {{ description }}
    </div>
    <div v-if="currentState.type !== 'success'" class="total">
      {{ total | amountWithCurrency }}
    </div>
    <large-btn
      class="primary-confirm-dialog_btn primary-confirm-dialog_btn__confirm terminal-payment-view_btn"
      color="primary"
      height="64px"
      :uppercase="false"
      block
      :disabled="buttonDisabled"
      @click="executeAction"
    >
      <font-awesome-icon v-if="currentState.buttonIcon" :icon="currentState.buttonIcon" class="mr-2" />
      {{ currentState.buttonText }}
    </large-btn>
  </section>
</template>

<script>
  import IconLoading from '@/components/transaction-components/IconLoading.vue'
  import IconTerminal from '@/components/icons/IconTerminal.vue'
  import IconCheckCircle from '@/components/icons/IconCheckCircle.vue'
  import IconAlertCircle from '@/components/icons/IconAlertCircle.vue'
  import IconTerminalManual from '@/components/icons/IconTerminalManual.vue'
  import LargeBtn from '@/components/ui/buttons/LargeBtn.vue'
  import snackbarMessage from '@/mixins/snackbar-message'
  import terminalCountdown from '@/mixins/terminal-countdown'
  import PaymentType from '@/models/enums/PaymentType'
  import paymentApi from '@/api/order/payment'
  import { waitForTerminalOperation } from '@/services/terminal-operation-poller'
  import { trackInBackground, untrack } from '@/services/terminal-operation-tracker'

  export default {
    name: 'TerminalPaymentView',
    components: { IconLoading, IconTerminal, IconCheckCircle, IconAlertCircle, IconTerminalManual, LargeBtn },
    mixins: [snackbarMessage, terminalCountdown],
    props: {
      orderId: {
        type: [String, Number],
        default: null,
      },
      orderCode: {
        type: [String, Number],
        default: null,
      },
      total: Number,
      loading: {
        type: Boolean,
        default: false,
      },
      paymentSettings: {
        type: Object,
        default: () => null,
      },
      manual: Boolean,
      // незавершённая оплата с прошлого открытия окна: { operationId, timeoutSeconds }
      activeOperation: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        currentState: null,
        poller: null,
        // операция, запущенная на бэке и ещё не завершённая: живёт до дедлайна даже после ухода в ручной режим
        pendingOperationId: null,
        // причина последней неудачной записи (error операции или 422 ручного повтора)
        applyError: null,
        // причина «терминал ответил, оплата не закрыта» - из error операции
        resultError: null,
        retryingApply: false,
        // токен попытки «Повторить сейчас»: ответ старой попытки после закрытия окна не трогает состояние
        applyAttempt: 0,
        states: {
          start: {
            type: 'start',
            title: 'Оплата картой на терминале',
            icon: IconTerminal,
            buttonText: 'Начать оплату',
          },
          success: {
            type: 'success',
            title: 'Оплата завершена!',
            icon: IconCheckCircle,
            buttonIcon: 'check',
            buttonText: 'Готово',
          },
          error: {
            type: 'error',
            title: 'Проблема с терминалом, попробуйте еще раз...',
            icon: IconAlertCircle,
            buttonIcon: 'redo-alt',
            buttonText: 'Повторить оплату',
          },
          manual: {
            type: 'manual',
            title: 'Оплата вручную',
            icon: IconTerminalManual,
            buttonIcon: 'check',
            buttonText: 'Оплата произведена',
          },
          loading: {
            type: 'loading',
            title: 'Ожидаем ответа от терминала...',
            icon: IconLoading,
            buttonIcon: 'check',
            buttonText: 'Оплата произведена',
          },
          checking: {
            type: 'loading',
            title: 'Терминал не ответил, проверяем результат...',
            icon: IconLoading,
            buttonIcon: 'check',
            buttonText: 'Оплата произведена',
          },
          // терминал списал деньги, учёт не записан: повторять оплату нельзя, только запись
          applying: {
            type: 'applying',
            title: 'Деньги списаны, оплата записывается',
            icon: IconAlertCircle,
            buttonIcon: 'redo-alt',
            buttonText: 'Повторить сейчас',
          },
          // терминал ответил успехом, а оплата не закрыта (нет идентификатора операции, заказ оплачен иначе
          // или отменён): деньги могли списаться - повтор на терминале запрещён, только ручное закрытие
          confirmedUnknown: {
            type: 'confirmedUnknown',
            title: 'Терминал ответил, оплата не закрыта',
            icon: IconAlertCircle,
            buttonIcon: 'check',
            buttonText: 'Закрыть оплату вручную',
          },
        },
      }
    },
    computed: {
      isLoading: {
        get () { return this.loading },
        set (newValue) { this.$emit('update:loading', newValue) },
      },
      isManual () {
        return this.currentState.type === 'manual'
      },
      isApplyingState () {
        return this.currentState === this.states.applying
      },
      buttonDisabled () {
        return this.isLoading || this.currentState.type === 'loading' || this.retryingApply
      },
      description () {
        switch (this.currentState) {
          case this.states.loading: return this.terminalWaitingHint()
          case this.states.checking: return 'Проверяем результат на терминале. Если терминал завис, нажмите «Вручную» или закройте окно.'
          case this.states.error: return 'Попробуйте ещё раз или примите оплату сами, нажав «Вручную».'

          case this.states.applying: {
            const hint = 'Терминал провёл оплату, CRM записывает её. Можно закрыть окно - запись повторится автоматически.'
            return this.applyError ? `${this.applyError}. ${hint}` : hint
          }

          case this.states.confirmedUnknown:
            return `${this.resultError}. Сверьте с терминалом: если деньги списаны - закройте оплату вручную картой. Повторять оплату на терминале нельзя.`

          case this.states.manual:
            if (this.pendingOperationId) {
              return 'Терминал ещё может завершить операцию. Убедитесь, что клиент не оплатил картой, и только потом подтверждайте.'
            }
            return 'Введите сумму на терминале и примите оплату картой.'

          default: return null
        }
      },
      // команду выполнит вкладка-лидер филиала, локальный прокси на этой машине не нужен
      isKkmServerPaymentEnabled () {
        return Boolean(this.paymentSettings?.available)
      },
    },
    watch: {
      'currentState.type' (type) {
        this.$emit('state', type)
      },
      // режим переключает только оператор кнопкой в родителе: провал терминала оставляет в состоянии ошибки
      manual (value) {
        if (value === this.isManual) return
        value ? this.switchToManual() : this.switchToAuto()
      },
    },
    created () {
      this.initState()
      if (this.activeOperation && this.isKkmServerPaymentEnabled) {
        this.resumeOperation(this.activeOperation)
      }
    },
    beforeDestroy () {
      this.handOffOperation()
      this.stopPolling()
    },
    methods: {
      initState () {
        this.stopPolling()
        this.stopCountdown()
        this.pendingOperationId = null
        this.applyError = null
        this.resultError = null
        this.retryingApply = false
        this.isLoading = false
        this.currentState = this.isKkmServerPaymentEnabled ? this.states.start : this.states.manual
      },
      switchToManual () {
        this.stopPolling()
        this.isLoading = false
        this.currentState = this.states.manual
      },
      // назад к терминалу: незавершённую операцию не дублируем, а снова ждём её результат
      switchToAuto () {
        if (this.pendingOperationId) {
          this.currentState = this.states.loading
          this.isLoading = true
          this.watchOperation(this.pendingOperationId)
          return
        }

        this.currentState = this.states.start
      },
      // окно закрыли, не дождавшись терминала или записи: итог покажет и карточку обновит фоновый опрос
      handOffOperation () {
        if (!this.pendingOperationId) return

        const order = this.orderCode ? `Заказ №${this.orderCode}: ` : ''
        trackInBackground({
          orderId: this.orderId,
          operationId: this.pendingOperationId,
          expectedPaymentStatus: 'success',
          successText: `${order}оплата на терминале прошла`,
          unappliedText: () => `${order}деньги списаны, оплата записывается - проверьте заказ позже`,
          failText: `${order}терминал не провёл оплату`,
        })
        this.pendingOperationId = null
      },
      stopPolling () {
        if (this.poller) {
          this.poller.cancel()
          this.poller = null
        }
      },
      // true - оплата записана: окно закрывается только тогда, 422 оставляет его открытым
      async pay (data) {
        if (!this.orderId) {
          return false
        }

        this.isLoading = true
        try {
          await paymentApi.complete(this.orderId, { type: PaymentType.Terminal, ...data })
          return true
        } catch (e) {
          this.showErrorFromResponse(e)
          return false
        } finally {
          this.isLoading = false
        }
      },
      executeAction () {
        switch (this.currentState.type) {
          case 'start':
          case 'error': this.startCardPayment(); break

          case 'success': this.$emit('paid'); break

          case 'applying': this.retryApply(); break

          // родитель держит :manual.sync - watch manual переведёт окно в ручной режим
          case 'confirmedUnknown': this.$emit('update:manual', true); break

          case 'manual': this.pay().then(paid => { if (paid) this.$emit('paid') }); break
        }
      },
      async startCardPayment () {
        if (!this.isKkmServerPaymentEnabled) {
          this.showErrorMessage('Оплата картой недоступна')
          return
        }

        this.currentState = this.states.loading
        this.isLoading = true

        try {
          const { operationId, timeoutSeconds } = await paymentApi.terminalPay(this.orderId)
          this.pendingOperationId = operationId
          // окно закрыли, пока команда создавалась
          if (this._isDestroyed) {
            this.handOffOperation()
            return
          }
          this.startCountdown(timeoutSeconds)
          await this.watchOperation(operationId)
        } catch (e) {
          this.handleError(this.getErrorMessageFromResponse(e) || e?.message || 'Неизвестная ошибка терминала')
        }
      },
      async resumeOperation (operation) {
        const { operationId, timeoutSeconds } = operation
        untrack(operationId)

        // терминал уже списал деньги, учёт ещё пишется: сразу «записывается», терминал не ждём
        if (operation.status === 'success' && operation.applied === false) {
          this.enterApplying(operationId, operation)
          return
        }

        this.currentState = this.states.loading
        this.isLoading = true
        this.pendingOperationId = operationId
        this.startCountdown(timeoutSeconds)
        await this.watchOperation(operationId)
      },
      async watchOperation (operationId) {
        this.poller = waitForTerminalOperation(() => paymentApi.fetchTerminalOperation(this.orderId, operationId), {
          onTick: operation => {
            if (operation.status === 'unknown' && this.currentState.type === 'loading') {
              this.currentState = this.states.checking
            }

            // деньги списаны, учёт ещё пишется: льготное окно поллера идёт в «записывается» - без «Вручную»
            // и без ложной «Проблемы с терминалом»; кнопка ждёт итога окна
            if (operation.status === 'success' && operation.applied === false && !this.isApplyingState) {
              this.stopCountdown()
              this.enterApplying(operationId, operation)
              this.retryingApply = true
            }
          },
        })

        try {
          const operation = await this.poller.promise
          this.handleOperationResult(operation)
        } catch (e) {
          this.handleError(this.getErrorMessageFromResponse(e) || e?.message || 'Неизвестная ошибка терминала')
        }
      },
      handleOperationResult (operation) {
        this.poller = null
        this.retryingApply = false
        this.stopCountdown()

        // терминал списал, учёт не записан за льготное окно: операцию держим - её повторяют кнопкой,
        // а если окно закроют, досматривает фоновый трекер
        if (operation.status === 'success' && operation.applied === false) {
          this.enterApplying(operation.id, operation)
          return
        }

        this.pendingOperationId = null

        if (operation.status === 'success' && operation.paymentStatus === 'success') {
          this.currentState = this.states.success
          this.isLoading = false
          return
        }

        // терминал ответил успехом, бэк оплату не закрыл: повтор на терминале - второе списание (бэк его и не пустит)
        if (operation.status === 'success') {
          this.resultError = operation.error || 'Терминал подтвердил оплату, но платёж не закрыт'
          this.isLoading = false
          this.currentState = this.states.confirmedUnknown
          return
        }

        this.handleError(operation.error || 'Неизвестная ошибка терминала')
      },
      // operation - ответ бэка (операция или active): error - причина последней неудачной записи.
      // Ручной повтор причину в операцию не пишет: свежую из его 422 не затираем пустой
      enterApplying (operationId, operation) {
        this.pendingOperationId = operationId
        this.applyError = operation.error || this.applyError
        this.retryingApply = false
        this.isLoading = false
        this.currentState = this.states.applying
      },
      // «Повторить сейчас»: запись по кнопке, итог забирает поллер с тем же льготным окном - записалось:
      // окно встаёт в «Оплата завершена», нет - снова «записывается»
      async retryApply () {
        const operationId = this.pendingOperationId
        if (!operationId) return

        const attempt = ++this.applyAttempt
        this.retryingApply = true
        let error = null
        try {
          await paymentApi.applyTerminalOperation(this.orderId, operationId)
        } catch (e) {
          error = e
        }

        // окно закрыли, пока шёл apply: второй опрос той же операции дал бы двойной итог
        if (attempt !== this.applyAttempt || this._isDestroyed) return

        if (error) {
          // бэк вернул причину (422): показываем её в окне. Сеть или 5xx - не причина записи: только снекбар
          const status = error?.response?.status
          if (status >= 400 && status < 500) {
            this.applyError = this.getErrorMessageFromResponse(error) || this.applyError
          } else {
            this.showErrorMessage(this.getErrorMessageFromResponse(error) || 'Не удалось повторить запись')
          }
        }

        await this.watchOperation(operationId)
      },
      handleError (errorText) {
        this.poller = null
        this.retryingApply = false
        this.stopCountdown()
        this.showErrorMessage(errorText)
        // деньги списаны, учёт пишется: сбой опроса не повод забыть операцию - её повторяют кнопкой,
        // а при закрытии окна досматривает фоновый трекер
        if (this.isApplyingState && this.pendingOperationId) return

        this.pendingOperationId = null
        this.isLoading = false
        this.currentState = this.states.error
      },
    },
  }
</script>

<style lang="less" scoped>
.terminal-payment-view {
    padding: 15px 27px;
    height: 550px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    // block-кнопка Vuetify имеет flex: 1 0 auto и растягивается на всю колонку
    .v-btn--block {
      flex-grow: 0;
    }

    // середина кнопки по одной линии с плиткой «Карта» слева; иконку и сумму над кнопкой не двигает
    &_btn {
      position: relative;
      top: 22px;
    }
}
.terminal-payment {
  &-title {
    font-size: 26px;
    font-weight: 500;
    line-height: 1.2;
    letter-spacing: -0.01em;
    text-align: center;
    color: #111;
  }

  &-description {
    font-size: 18px;
    line-height: 1.4;
    text-align: center;
    text-wrap: balance;
    color: #6b6b6b;
  }
}
.total {
    color: #161616;
    font-size: 42px;
    text-align: center;
  }
</style>
